import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Flex, Input, Text, VStack } from '@chakra-ui/react';
import { FaWhatsapp } from 'react-icons/fa';
import { InputGroup } from '../../ui/input-group';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogCloseTrigger,
} from '../../ui/dialog';
import {
  addPhoneNumberRequest,
  resetBusinessOnboarding,
  verifyPhoneNumberRequest,
} from '../../../store/businessOnboardingSlice';

const validatePhone = (phone) => {
  const stripped = phone.replace(/\s+/g, '');
  return /^\+91\d{10}$/.test(stripped);
};

const validateOtp = (otp) => /^\d{6}$/.test(String(otp || '').trim());

const OptionalBusinessDetailsDialog = ({
  open,
  initialPhoneNumber = '',
  onClose,
  onComplete,
}) => {
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [otp, setOtp] = useState('');
  const dispatch = useDispatch();
  const isSubmitting = useSelector((state) => state.businessOnboarding.loading);
  const onboardingStep = useSelector((state) => state.businessOnboarding.step);
  const [errors, setErrors] = useState({
    phoneNumber: false,
    otp: false,
  });

  useEffect(() => {
    if (open) {
      setPhoneNumber(initialPhoneNumber);
      setOtp('');
      setErrors({ phoneNumber: false, otp: false });
      dispatch(resetBusinessOnboarding());
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (open && onboardingStep === 'PHONE') {
      setPhoneNumber(initialPhoneNumber);
    }
  }, [open, initialPhoneNumber, onboardingStep]);

  useEffect(() => {
    if (onboardingStep === 'COMPLETE') {
      onComplete?.();
      onClose?.();
    }
  }, [onboardingStep, onClose, onComplete]);

  const handleContinue = () => {
    const nextErrors = onboardingStep === 'OTP'
      ? {
          phoneNumber: false,
          otp: !validateOtp(otp),
        }
      : {
          phoneNumber: !validatePhone(phoneNumber),
          otp: false,
        };

    setErrors(nextErrors);

    if (onboardingStep === 'OTP') {
      if (nextErrors.otp) {
        return;
      }

      dispatch(verifyPhoneNumberRequest({
        otp: otp.trim(),
      }));
      return;
    }

    if (nextErrors.phoneNumber) {
      return;
    }

    dispatch(addPhoneNumberRequest({
      phoneNumber: phoneNumber.trim(),
    }));
  };

  return (
    <DialogRoot open={open} onOpenChange={({ open: nextOpen }) => { if (!nextOpen) onClose?.(); }}>
      <DialogContent
        placement="center"
        maxWidth="440px"
        borderRadius="24px"
        bg="white"
        p={0}
        overflow="hidden"
        boxShadow="0 4px 24px rgba(0, 0, 0, 0.06)"
        border="1px solid"
        borderColor="gray.100"
      >
        <DialogHeader px={8} pt={8} pb={0} pr={12}>
          <VStack align="start" gap={2}>
            <DialogTitle fontSize="28px" fontWeight="700" color="gray.900" lineHeight="1.15">
              {onboardingStep === 'OTP' ? 'Verify your Phone number' : 'One last step!'}
            </DialogTitle>
            <Text fontSize="14px" color="gray.500" lineHeight="1.6">
              {onboardingStep === 'OTP'
                ? 'Please enter the OTP sent to your WhatsApp to finish setup.'
                : 'Please add your Phone number to receive setup help and important updates.'}
            </Text>
          </VStack>
          <DialogCloseTrigger />
        </DialogHeader>

        <DialogBody px={8} py={8}>
          <VStack gap={5} align="stretch">
            <Box>
              <Text fontSize="13px" fontWeight="500" color="gray.700" mb={2}>
                Phone Number
              </Text>
              <InputGroup startElement={<FaWhatsapp color="#94a3b8" />} w="100%">
                <Input
                  value={phoneNumber}
                  onChange={(e) => {
                    setPhoneNumber(e.target.value);
                    if (errors.phoneNumber) {
                      setErrors((prev) => ({ ...prev, phoneNumber: false }));
                    }
                  }}
                  placeholder="+91 98765 43210"
                  h="44px"
                  borderRadius="8px"
                  borderColor={errors.phoneNumber ? 'red.500' : 'gray.200'}
                  bg="white"
                  fontSize="15px"
                  isDisabled={onboardingStep === 'OTP'}
                  _focusVisible={{ borderColor: '#0f6e56', boxShadow: '0 0 0 1px #0f6e56' }}
                />
              </InputGroup>
              {errors.phoneNumber ? (
                <Text mt={1} fontSize="12px" color="red.500">
                  Please enter a valid number (e.g. +91 98765 43210)
                </Text>
              ) : onboardingStep === 'OTP' ? (
                <Text mt={1} fontSize="13px" color="gray.500" lineHeight="1.5">
                  OTP sent to WhatsApp on this number.
                </Text>
              ) : (
                <Text mt={1} fontSize="13px" color="gray.500" lineHeight="1.5">
                  {initialPhoneNumber
                    ? 'We found your number. Click Send OTP to verify it.'
                    : "We'll send a welcome message here to confirm your trial."}
                </Text>
              )}
            </Box>

            {onboardingStep === 'OTP' && (
              <Box>
                <Text fontSize="13px" fontWeight="500" color="gray.700" mb={2}>
                  OTP
                </Text>
                <Input
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value);
                    if (errors.otp) {
                      setErrors((prev) => ({ ...prev, otp: false }));
                    }
                  }}
                  placeholder="Enter 6-digit OTP"
                  h="44px"
                  borderRadius="8px"
                  borderColor={errors.otp ? 'red.500' : 'gray.200'}
                  bg="white"
                  fontSize="15px"
                  _focusVisible={{ borderColor: '#0f6e56', boxShadow: '0 0 0 1px #0f6e56' }}
                />
                {errors.otp && (
                  <Text mt={1} fontSize="12px" color="red.500">
                    Please enter the 6-digit OTP
                  </Text>
                )}
              </Box>
            )}
          </VStack>
        </DialogBody>

        <DialogFooter px={8} pb={8} pt={2}>
          <Flex w="full" gap={3} justify="flex-end">
              <Button
                onClick={handleContinue}
              h="44px"
              px={8}
              borderRadius="6px"
              bg="#0f6e56"
              color="white"
              fontSize="15px"
              fontWeight="500"
              _hover={{ bg: '#1d9e75' }}
              boxShadow="0 1px 2px rgba(0, 0, 0, 0.08)"
              flex="1"
                disabled={isSubmitting}
              >
              {isSubmitting ? 'Setting up...' : onboardingStep === 'OTP' ? 'Verify OTP' : 'Send OTP'}
              </Button>
          </Flex>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default OptionalBusinessDetailsDialog;
